import { z } from 'zod';

import { pageMetaSchema } from '@/lib/api/pagination';
import { emailAddressSchema } from '@/lib/validation/email';
import { nameFieldSchema } from '@/lib/validation/name';
import { usPhoneSchema } from '@/lib/validation/phone';

export const dealerStatusSchema = z.enum(['active', 'inactive', 'pending_activation']);
export const dealerRowActionSchema = z.enum(['edit', 'disable', 'enable', 'delete']);

/** One `GET /dealers/admin` row (greenway_api `DealerController` L102-161). `id` is the Dealer's
 *  `admin_users` id (ADR-0036); `uniqueId` is the unpadded `DLR-n`, displayed as sent.
 *  `assignedCustomerCount` is FE-proposed (grilling F1) and reads as 0 when absent. */
export const dealerRowSchema = z.object({
  id: z.string(),
  uniqueId: z.string(),
  companyName: z.string(),
  dealerName: z.string(),
  cityLabel: z.string(),
  email: z.string(),
  phone: z.string(),
  status: dealerStatusSchema,
  actions: z.array(dealerRowActionSchema),
  canDelete: z.boolean(),
  unassignedDeviceCount: z.number(),
  assignedCustomerCount: z.number().optional(),
});

export const dealerListResponseSchema = z.object({
  data: z.array(dealerRowSchema),
  meta: pageMetaSchema,
});

/** `GET /dealers/admin/:dealerId`. Only the fields the detail header reads are declared; zod strips
 *  the rest. Edit (ticket 08) adds its own. */
export const dealerProfileSchema = z.object({
  id: z.string(),
  uniqueId: z.string(),
  dealerName: z.string(),
  phone: z.string(),
  email: z.string(),
  companyName: z.string(),
  cityLabel: z.string(),
  status: dealerStatusSchema,
  ordersCount: z.number(),
  // FE-proposed (grilling F4): the Dealer's own `customers` id, the Self Assigned View Details
  // destination. Optional until BE ships it; absent hides that column.
  customerAccountId: z.string().optional(),
});

/**
 * The six Device Status tokens. Deliberately duplicated from `customerDeviceStatusSchema` rather
 * than imported, for the reason `customer-map/schemas.ts` gives: the customers barrel re-exports
 * React, and `schemas.test.ts` runs in the DOM-less `node` project. Root `CONTEXT.md` owns the
 * tokens; if it changes, every copy changes.
 */
export const dealerDeviceStatusSchema = z.enum([
  'pending_configuration',
  'pending_confirmation',
  'pending_qc',
  'running',
  'off',
  'disconnected',
]);
export const dealerDeviceTypeSchema = z.enum(['dm_device', 'cropx', 'other']);
export const dealerDeviceAllocationSchema = z.enum(['unassigned', 'self_assigned', 'sold']);

/** `active`/`expired` (ADO 112576). Duplicated from customers for the `node`-project reason above. */
export const dealerSubscriptionStatusSchema = z.enum(['active', 'expired']);

/** One `GET /dealers/admin/:dealerId/devices` row. Self Assigned / Sold add their fields; the BE omits
 *  rather than nulls the ones a tab lacks, and the page knows which tab it is rendering. */
export const dealerDeviceRowSchema = z.object({
  id: z.string(),
  category: z.string(),
  deviceName: z.string(),
  deviceType: dealerDeviceTypeSchema,
  serialNo: z.string().nullable(),
  deviceStatus: dealerDeviceStatusSchema,
  subscriptionStatus: dealerSubscriptionStatusSchema.optional(),
  lastActive: z.string().nullable().optional(),
  // `string | null` in the BE types: a unit allocated before `allocated_on` existed has no date.
  assignedOn: z.string().nullable().optional(),
  soldOn: z.string().nullable().optional(),
  soldTo: z.object({ customerId: z.string(), name: z.string() }).optional(),
});

export const dealerDeviceListResponseSchema = z.object({
  data: z.array(dealerDeviceRowSchema),
  meta: pageMetaSchema,
});

const filterOptionSchema = z.object({ value: z.string(), label: z.string() });

/** `…/meta/filters?allocation=`. `subscriptionStatus`/`dateRange` are absent on unassigned; the date
 *  label travels with the options (BE `DEALER_DEVICE_DATE_LABELS`). `min`/`max` are not read. */
export const dealerDeviceFilterOptionsSchema = z.object({
  category: z.array(z.string()),
  deviceType: z.array(filterOptionSchema),
  deviceStatus: z.array(filterOptionSchema),
  subscriptionStatus: z.array(filterOptionSchema).optional(),
  dateRange: z.object({ label: z.string() }).optional(),
});

export const dealerDeviceFilterFormSchema = z.object({
  category: z.array(z.string()),
  deviceType: z.array(z.string()),
  deviceStatus: z.array(z.string()),
  subscriptionStatus: z.array(z.string()),
  dateFrom: z.string().nullish(),
  dateTo: z.string().nullish(),
});

/** Company Name's permitted set (BE `CreateDealerDto`); the input strips everything else as typed. */
const COMPANY_NAME_CHARS = "A-Za-z0-9 &.,\\-'()/";
export const COMPANY_NAME_DISALLOWED = new RegExp(`[^${COMPANY_NAME_CHARS}]`, 'g');

const usCitySchema = z.object({ city: z.string(), state: z.string() });
type UsCitySelection = z.infer<typeof usCitySchema>;

/** Create Dealer form (118643). Messages are AC-verbatim; City's is the BE's own
 *  (`'Select a city from the list.'`) since the AC words none. City is `null` in the form until an
 *  option is picked; the refine narrows it, so the parsed output's `city` is never `null`. */
export const buildDealerFormSchema = (storedPhone?: string) =>
  z.object({
    firstName: nameFieldSchema,
    lastName: nameFieldSchema,
    // On Edit, `storedPhone` lets an unchanged number that predates the US rule through (ADR-0035).
    phone: usPhoneSchema('Enter a valid 10-digit US phone number', storedPhone),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .max(100, 'Enter a valid email address')
      .pipe(emailAddressSchema('Enter a valid email address')),
    companyName: z
      .string()
      .trim()
      .min(1, 'Enter a valid company name')
      .max(250, 'Enter a valid company name')
      .regex(new RegExp(`^[${COMPANY_NAME_CHARS}]+$`), 'Enter a valid company name'),
    city: usCitySchema
      .nullable()
      .refine((v): v is UsCitySelection => v !== null, 'Select a city from the list.'),
  });

export const dealerFormSchema = buildDealerFormSchema();

/** Form field names, so a server `details[].field` maps only onto a rendered input (ADO 118281). */
export const DEALER_FORM_FIELDS = Object.keys(dealerFormSchema.shape);

/** `GET /dealers/admin/:dealerId` (BE §1.5), as far as the Edit prefill reads it. `updatedAt` is
 *  echoed back as `expectedUpdatedAt` (grilling Q14). */
export const dealerDetailSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string(),
  email: z.string(),
  companyName: z.string(),
  city: z.string(),
  state: z.string(),
  updatedAt: z.string(),
});

/** `PUT /dealers/admin/:dealerId` response (BE §1.6), as far as the client reads it. */
export const dealerUpdateResultSchema = z.object({ changed: z.boolean() });

/** `GET /dealers/admin/meta/us-cities` page (BE §1.3). */
export const usCitiesPageSchema = z.object({
  data: z.array(z.object({ city: z.string(), state: z.string(), label: z.string() })),
  meta: z.object({ hasNextPage: z.boolean(), nextCursor: z.string().nullish() }),
});

/** `GET /dealers/admin/meta/cities` (BE L167-195): only cities present in dealer data. `label`
 *  ("City, ST") is exactly what the listing's `city` filter matches, so it is the option value. */
export const dealerCityOptionSchema = z.object({
  city: z.string(),
  state: z.string(),
  label: z.string(),
});

export const dealerCityPageSchema = z.object({
  data: z.array(dealerCityOptionSchema),
  meta: z.object({
    hasNextPage: z.boolean(),
    nextCursor: z
      .string()
      .nullish()
      .transform((c) => c ?? undefined),
  }),
});
